'use client';

import 'BomdisoftComponents/inputs/token-field/styles.css';
import { CloseRoundedIcon } from 'BomdisoftComponents/icons/index';
import { tokenKeyAction } from 'BomdisoftComponents/inputs/token-field/render';
import { placeAnchoredPopover } from 'BomdisoftComponents/menu/placement';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { type ChangeEvent, type KeyboardEvent, type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';

export type Token = { key: string; label: string; mark?: ReactNode };

export type TokenOption = { key: string; label: string; meta?: string; mark?: ReactNode; variant?: 'action' };

export type TokenSuggestionGroup = { key: string; label?: string; options: TokenOption[] };

export type { TokenKeyAction, TokenKeyState } from 'BomdisoftComponents/inputs/token-field/render';
export { tokenKeyAction } from 'BomdisoftComponents/inputs/token-field/render';

export type TokenFieldProps = {
  label: string;
  tokens: Token[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onCommit: (text: string) => boolean;
  onRemove: (key: string) => void;
  removeLabel: (token: Token) => string;
  suggestions?: TokenSuggestionGroup[];
  onPick?: (option: TokenOption) => void;
  placeholder?: string;
  helperText?: string;
  errorText?: string;
  disabled?: boolean;
  inline?: boolean;
  commitOnBlur?: boolean;
  testId?: string;
};

export const TokenField = ({ commitOnBlur = false, disabled, errorText, helperText, inline = false, inputValue, label, onCommit, onInputChange, onPick, onRemove, placeholder, removeLabel, suggestions, testId, tokens }: TokenFieldProps) => {
  const id = useId();
  const listboxId = `${id}-listbox`;
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const refused = errorText !== undefined && errorText !== '';
  const options = (suggestions ?? []).flatMap(group => group.options);
  const offersSuggestions = suggestions !== undefined;
  const showing = open && offersSuggestions && options.length > 0 && !disabled;
  const activeOption = activeIndex >= 0 ? options[activeIndex] : undefined;

  const place = useCallback(() => {
    const list = listRef.current;
    const field = fieldRef.current;
    if (!list || !field) return;
    const anchor = field.getBoundingClientRect();
    list.style.setProperty('--tf-width', `${anchor.width}px`);
    list.style.maxHeight = '';
    const position = placeAnchoredPopover(anchor, { height: list.getBoundingClientRect().height, width: anchor.width }, { height: window.innerHeight, width: window.innerWidth });
    list.style.setProperty('--tf-left', `${position.left}px`);
    list.style.setProperty('--tf-top', `${position.top}px`);
    if (position.maxHeight !== undefined) list.style.maxHeight = `${position.maxHeight}px`;
  }, []);

  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof list.showPopover !== 'function') return;
    if (!showing) {
      if (list.matches(':popover-open')) list.hidePopover();
      return;
    }
    if (!list.matches(':popover-open')) {
      list.showPopover();
      place();
      requestAnimationFrame(place);
    }
  }, [showing, place]);

  useEffect(() => {
    if (!showing) return undefined;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [showing, place]);

  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return undefined;
    const handleFieldClick = () => {
      if (!disabled) inputRef.current?.focus();
    };
    field.addEventListener('click', handleFieldClick);
    return () => field.removeEventListener('click', handleFieldClick);
  }, [disabled]);

  const pick = (option: TokenOption) => {
    onPick?.(option);
    onInputChange('');
    setActiveIndex(-1);
    setOpen(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setActiveIndex(-1);
    setOpen(true);
    onInputChange(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const action = tokenKeyAction({ activeIndex, inputValue, key: event.key, optionCount: offersSuggestions ? options.length : 0, showing, tokenCount: tokens.length });
    if (action.kind === 'none') {
      if (event.key === 'Enter' || event.key === ',') event.preventDefault();
      return;
    }
    if (action.kind !== 'removeLast') event.preventDefault();
    if (action.kind === 'move') {
      setOpen(true);
      setActiveIndex(action.index);
    } else if (action.kind === 'pick' && activeOption !== undefined) {
      pick(activeOption);
    } else if (action.kind === 'commit') {
      if (onCommit(inputValue.trim())) onInputChange('');
    } else if (action.kind === 'removeLast') {
      onRemove(tokens[tokens.length - 1].key);
    } else if (action.kind === 'close') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const helper = refused ? errorText : helperText;
  const inputProps = {
    'aria-describedby': helper ? `${id}-helper` : undefined,
    'aria-invalid': refused ? true : undefined,
    autoComplete: 'off',
    className: 'bomdisoft-token-field-input',
    'data-testid': testId ? `${testId}-input` : undefined,
    disabled,
    id: `${id}-input`,
    onBlur: () => {
      setOpen(false);
      setActiveIndex(-1);
      if (commitOnBlur && inputValue.trim() !== '') onCommit(inputValue);
    },
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    placeholder,
    ref: inputRef,
    value: inputValue,
  };
  let optionIndex = -1;

  return (
    <div className='bomdisoft-token-field' data-inline={inline ? '' : undefined} data-testid={testId}>
      <label className='bomdisoft-token-field-label' data-disabled={disabled ? '' : undefined} data-error={refused ? '' : undefined} data-hidden={inline ? '' : undefined} htmlFor={`${id}-input`}>
        {label}
      </label>
      <div className='bomdisoft-token-field-field' data-disabled={disabled ? '' : undefined} data-error={refused ? '' : undefined} ref={fieldRef}>
        {tokens.map(token => (
          <span className='bomdisoft-token-field-token' data-lead={token.mark ? '' : undefined} data-testid={testId ? `${testId}-token-${token.key}` : undefined} key={token.key}>
            {token.mark}
            <span className='bomdisoft-token-field-token-label'>{token.label}</span>
            {disabled ? null : (
              <Tooltip placement='top' title={removeLabel(token)}>
                <button
                  aria-label={removeLabel(token)}
                  className='bomdisoft-token-field-remove'
                  data-testid={testId ? `${testId}-remove-${token.key}` : undefined}
                  onClick={event => {
                    event.stopPropagation();
                    onRemove(token.key);
                  }}
                  type='button'
                >
                  <CloseRoundedIcon size={13} />
                </button>
              </Tooltip>
            )}
          </span>
        ))}
        <span className='bomdisoft-token-field-entry' data-placeholder={placeholder}>
          {offersSuggestions ? <input {...inputProps} aria-activedescendant={showing && activeOption !== undefined ? `${listboxId}-option-${activeIndex}` : undefined} aria-autocomplete='list' aria-controls={listboxId} aria-expanded={showing} role='combobox' /> : <input {...inputProps} />}
        </span>
      </div>
      {helper ? (
        <p className='bomdisoft-token-field-helper' data-disabled={disabled ? '' : undefined} data-error={refused ? '' : undefined} data-testid={testId ? `${testId}-helper` : undefined} id={`${id}-helper`}>
          {helper}
        </p>
      ) : null}
      {offersSuggestions ? (
        <div aria-label={label} className='bomdisoft-token-field-listbox' id={listboxId} popover='manual' ref={listRef} role='listbox'>
          {(suggestions ?? []).map((group, groupIndex) => (
            <fieldset aria-labelledby={group.label ? `${listboxId}-group-${groupIndex}` : undefined} className='bomdisoft-token-field-group' data-testid={testId ? `${testId}-group-${group.key}` : undefined} key={group.key}>
              {group.label ? (
                <div className='bomdisoft-token-field-group-label' id={`${listboxId}-group-${groupIndex}`}>
                  {group.label}
                </div>
              ) : null}
              {group.options.map(option => {
                optionIndex += 1;
                const index = optionIndex;
                return (
                  <div
                    aria-selected={index === activeIndex}
                    className='bomdisoft-token-field-option'
                    data-active={index === activeIndex ? '' : undefined}
                    data-testid={testId ? `${testId}-option-${option.key}` : undefined}
                    data-variant={option.variant}
                    id={`${listboxId}-option-${index}`}
                    key={option.key}
                    onMouseDown={event => {
                      if (event.button !== 0) return;
                      event.preventDefault();
                      pick(option);
                    }}
                    role='option'
                    tabIndex={-1}
                  >
                    {option.mark}
                    <span className='bomdisoft-token-field-option-words'>
                      <span>{option.label}</span>
                      {option.meta ? <span className='bomdisoft-token-field-meta'>{option.meta}</span> : null}
                    </span>
                  </div>
                );
              })}
            </fieldset>
          ))}
        </div>
      ) : null}
    </div>
  );
};
